import {useCallback, useEffect, useState} from 'react';

interface UseAiWriterRecentsParams<T> {
  fetchRecents: () => Promise<T[]>;
  getItemKey: (item: T) => string;
}

interface UseAiWriterRecentsReturn<T> {
  recents: T[];
  isRecentsLoading: boolean;
  addRecent: (item: T) => void;
}

export function useAiWriterRecents<T>({fetchRecents, getItemKey}: UseAiWriterRecentsParams<T>): UseAiWriterRecentsReturn<T> {
  const [recents, setRecents] = useState<T[]>([]);
  const [isRecentsLoading, setIsRecentsLoading] = useState<boolean>(true);

  const addRecent = useCallback(
    (item: T): void => {
      const key = getItemKey(item);
      setRecents((current) => [item, ...current.filter((existing) => getItemKey(existing) !== key)]);
    },
    [getItemKey]
  );

  useEffect(() => {
    let isActive = true;
    setIsRecentsLoading(true);

    void (async (): Promise<void> => {
      try {
        const fetched = await fetchRecents();
        if (isActive) {
          setRecents(fetched);
        }
      } catch (e) {
        console.error('AI writer recents fetch failed:', e);
        if (isActive) {
          setRecents([]);
        }
      } finally {
        if (isActive) {
          setIsRecentsLoading(false);
        }
      }
    })();

    return (): void => {
      isActive = false;
    };
  }, [fetchRecents]);

  return {recents, isRecentsLoading, addRecent};
}
